import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, X, ChevronDown, SlidersHorizontal, Search } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { ScrollArea } from '../../ui/scroll-area';
import { BottomSheet } from '../../ui/BottomSheet';
import { cn } from '../../../lib/utils';

interface Option {
  value: string;
  label: string;
}

interface FilterGroup {
  label: string;
  options: Option[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  multiSelect?: boolean;
}

interface HorizontalFilterBarProps {
  filterGroups: FilterGroup[];
  onClearAll?: () => void;
  hasActiveFilters?: boolean;
  showSort?: boolean;
  onSort?: () => void;
  inline?: boolean;
}

// Threshold: groups with more options than this use a compact list with search
const COMPACT_LIST_THRESHOLD = 8;

function FilterGroupSection({ group, onOptionClick }: { group: FilterGroup; onOptionClick: (group: FilterGroup, option: Option) => void }) {
  const [search, setSearch] = useState('');
  const isLargeGroup = group.options.length > COMPACT_LIST_THRESHOLD;
  const filteredOptions = isLargeGroup && search
    ? group.options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()))
    : group.options;

  if (isLargeGroup) {
    // Compact searchable list for large option sets (countries, categories)
    return (
      <div>
        <h3 className="text-sm font-semibold text-content mb-2">{group.label}</h3>
        {/* Search input */}
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-content/30" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${group.label.toLowerCase()}...`}
            className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent-text/30 focus:border-accent-text/30"
          />
        </div>
        {/* Selected chips */}
        {group.selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {group.selectedValues.map(val => {
              const opt = group.options.find(o => o.value === val);
              if (!opt) return null;
              return (
                <button
                  key={val}
                  onClick={() => onOptionClick(group, opt)}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-accent-text text-white rounded-full active:scale-95"
                >
                  {opt.label}
                  <X className="h-3 w-3" />
                </button>
              );
            })}
          </div>
        )}
        {/* Scrollable list */}
        <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/50">
          {filteredOptions.length === 0 ? (
            <div className="py-3 text-center text-sm text-content/40">No results</div>
          ) : (
            filteredOptions.map(option => {
              const isSelected = group.selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => onOptionClick(group, option)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors",
                    isSelected ? "bg-accent-text/5 text-content font-medium" : "text-content/70 hover:bg-gray-100"
                  )}
                >
                  <span className={cn(
                    "flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors",
                    isSelected ? "bg-accent-text border-accent-text" : "border-gray-300 bg-white"
                  )}>
                    {isSelected && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // Small group — use wrapping pill chips
  return (
    <div>
      <h3 className="text-sm font-semibold text-content mb-2">{group.label}</h3>
      <div className="flex flex-wrap gap-2">
        {group.options.map(option => {
          const isSelected = group.selectedValues.includes(option.value);
          return (
            <button
              key={option.value}
              onClick={() => onOptionClick(group, option)}
              className={cn(
                "px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 active:scale-95",
                isSelected
                  ? "bg-accent-text text-white"
                  : "bg-gray-100 text-content/70 hover:bg-gray-200"
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function HorizontalFilterBar({
  filterGroups,
  onClearAll,
  hasActiveFilters = false,
  showSort = false,
  onSort,
  inline = false
}: HorizontalFilterBarProps) {
  const { t } = useTranslation();
  const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const totalActiveFilters = filterGroups.reduce((sum, g) => sum + g.selectedValues.length, 0);

  const handleOptionClick = (group: FilterGroup, option: Option) => {
    if (group.multiSelect === false) {
      group.onChange([option.value]);
      setOpenPopovers(prev => ({ ...prev, [group.label]: false }));
    } else {
      const newValues = group.selectedValues.includes(option.value)
        ? group.selectedValues.filter(v => v !== option.value)
        : [...group.selectedValues, option.value];
      group.onChange(newValues);
    }
  };

  const removeFilter = (group: FilterGroup, value: string) => {
    group.onChange(group.selectedValues.filter(v => v !== value));
  };

  // Desktop filter content (popovers)
  const desktopFilterContent = (
    <>
      {showSort && onSort && (
        <Button variant="outline" size="sm" onClick={onSort} className="flex-shrink-0 h-9 rounded-full gap-2">
          <ArrowUpDown className="h-4 w-4" />
          <span className="hidden sm:inline">{t('filters.sortBy')}</span>
        </Button>
      )}

      {filterGroups.map((group) => {
        const hasSelection = group.selectedValues.length > 0;
        const isOpen = openPopovers[group.label] || false;
        return (
          <Popover key={group.label} open={isOpen} onOpenChange={(open) => setOpenPopovers(prev => ({ ...prev, [group.label]: open }))}>
            <PopoverTrigger asChild>
              <Button
                variant={hasSelection ? "default" : "outline"}
                size="sm"
                className={cn("flex-shrink-0 h-9 rounded-full gap-1.5", hasSelection && "bg-primary hover:bg-primary/90 text-primary-foreground")}
              >
                <span>{group.label}</span>
                {hasSelection && (
                  <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-white/20 text-white border-0">{group.selectedValues.length}</Badge>
                )}
                <ChevronDown className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")} />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0 bg-white rounded-xl border border-gray-200 shadow-lg" align="start" side="bottom" sideOffset={8}>
              <ScrollArea className="h-[300px]">
                <div className="p-2">
                  {group.options.map((option) => {
                    const isSelected = group.selectedValues.includes(option.value);
                    return (
                      <label key={option.value} className={cn("flex items-center px-3 py-2 rounded-md cursor-pointer hover:bg-accent transition-colors", isSelected && "bg-accent")}>
                        <input
                          type={group.multiSelect === false ? "radio" : "checkbox"}
                          checked={isSelected}
                          onChange={() => handleOptionClick(group, option)}
                          className={cn("mr-3", group.multiSelect === false ? "text-accent-text focus:ring-accent-text" : "rounded border-gray-300 text-accent-text focus:ring-accent-text")}
                        />
                        <span className="text-sm text-foreground">{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        );
      })}

      {filterGroups.map((group) =>
        group.selectedValues.map((value) => {
          const option = group.options.find(opt => opt.value === value);
          if (!option) return null;
          return (
            <Button key={`${group.label}-${value}`} variant="default" size="sm" onClick={() => removeFilter(group, value)} className="flex-shrink-0 h-9 rounded-full gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
              <span>{option.label}</span>
              <X className="h-3 w-3" />
            </Button>
          );
        })
      )}

      {hasActiveFilters && onClearAll && (
        <Button variant="outline" size="sm" onClick={onClearAll} className="flex-shrink-0 h-9 rounded-full gap-1.5 text-destructive border-destructive/20 hover:bg-destructive/10">
          <X className="h-3 w-3" />
          <span className="hidden sm:inline">{t('filters.clearAll')}</span>
        </Button>
      )}
    </>
  );

  // Mobile filter button
  const mobileFilterButton = (
    <button
      onClick={() => setShowMobileFilters(true)}
      className="flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors active:scale-95"
    >
      <SlidersHorizontal className="h-4 w-4" />
      <span>{t('filters.filters', 'Filters')}</span>
      {totalActiveFilters > 0 && (
        <span className="flex items-center justify-center h-5 min-w-[20px] px-1 text-xs font-bold bg-accent-text text-white rounded-full">
          {totalActiveFilters}
        </span>
      )}
    </button>
  );

  // Mobile bottom sheet content
  const mobileSheetContent = (
    <div className="px-5 py-4 space-y-5 pb-20 lg:pb-8">
      {filterGroups.map((group) => (
        <FilterGroupSection
          key={group.label}
          group={group}
          onOptionClick={handleOptionClick}
        />
      ))}

      {/* Clear + Apply buttons */}
      <div className="flex gap-3 -mx-5 px-5 pt-4 bg-white border-t border-gray-100">
        {hasActiveFilters && onClearAll && (
          <button
            onClick={() => onClearAll()}
            className="flex-1 py-3 text-sm font-medium text-content/60 border border-gray-200 rounded-xl hover:bg-gray-50 active:scale-[0.98] transition-all"
          >
            {t('filters.clearAll', 'Clear All')}
          </button>
        )}
        <button
          onClick={() => setShowMobileFilters(false)}
          className="flex-1 py-3 text-sm font-medium text-white bg-accent-text rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all"
        >
          {totalActiveFilters > 0
            ? `${t('filters.showResults', 'Show Results')} (${totalActiveFilters})`
            : t('filters.showResults', 'Show Results')}
        </button>
      </div>
    </div>
  );

  // Inline mode: used inside wrapper components (Practitioners, Venues, Jobs, Events)
  if (inline) {
    return (
      <>
        {/* Desktop: inline popover pills */}
        <span className="hidden lg:contents">
          {desktopFilterContent}
        </span>
        {/* Mobile: single filter button */}
        <span className="lg:hidden">
          {mobileFilterButton}
        </span>
        <BottomSheet
          isOpen={showMobileFilters}
          onClose={() => setShowMobileFilters(false)}
          title={t('filters.filters', 'Filters')}
          maxHeight={85}
        >
          {mobileSheetContent}
        </BottomSheet>
      </>
    );
  }

  // Non-inline mode: full standalone bar (e.g., Recipes)
  return (
    <>
      {/* Desktop: horizontal bar */}
      <div className="hidden lg:block border-b border-border/50 w-full mb-4">
        <div className="px-3 sm:px-4 lg:px-6 max-w-page mx-auto">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 sm:gap-2 py-2 min-w-max">
              {desktopFilterContent}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: compact filter bar */}
      <div className="lg:hidden border-b border-gray-200 w-full mb-4">
        <div className="px-4 py-2 flex items-center gap-2">
          {mobileFilterButton}
          {/* Show active filter chips on mobile */}
          {totalActiveFilters > 0 && (
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
              {filterGroups.flatMap(group =>
                group.selectedValues.map(value => {
                  const option = group.options.find(opt => opt.value === value);
                  if (!option) return null;
                  return (
                    <button
                      key={`mobile-${group.label}-${value}`}
                      onClick={() => removeFilter(group, value)}
                      className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-accent-text text-white rounded-full active:scale-95"
                    >
                      {option.label}
                      <X className="h-3 w-3" />
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <BottomSheet
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        title={t('filters.filters', 'Filters')}
        maxHeight={85}
      >
        {mobileSheetContent}
      </BottomSheet>
    </>
  );
}
